import { Anchor, anchored, atAnchor, Point } from '@st/geom'
import { RefObject, useLayoutEffect, useRef } from 'react'
import { getScrollGeometry, ScrollGeometry } from './viewport'

/**
 * React hook that keeps a scrollable container visually stable when its
 * dimensions change – most commonly when the user zooms in or out.
 *
 * The hook captures the current scroll geometry during normal interaction and,
 * whenever **any** value in `triggerDeps` updates, calculates the new
 * `scrollLeft`/`scrollTop` required to keep the previous visual centre in view.
 * This prevents disorienting "jumps" that would otherwise occur after a scale
 * change and helps readers stay on the same page or paragraph.
 *
 * @example
 * ```typescript
 * const scrollRef = useRef<HTMLDivElement>(null)
 * const scale = useZoomLevel() // custom hook returning current zoom factor
 *
 * useScrollPreservation(scrollRef, [scale])
 * ```
 *
 * @param scrollContainerRef - Ref pointing to the scrollable element whose
 *   position should be preserved.
 * @param triggerDeps - Dependency array signalling **when** the underlying
 *   geometry may have changed (e.g. the current zoom value). The preservation
 *   logic only runs when one of these dependencies changes.
 * @returns void
 */
export function useScrollPreservation(
  scrollContainerRef: RefObject<HTMLDivElement | null>,
  triggerDeps: ReadonlyArray<unknown>
) {
  const prevGeometryRef = useRef<ScrollGeometry>(null)

  // ------------------------------------------------------------
  // Keep a fresh snapshot of geometry during normal interaction
  // ------------------------------------------------------------
  useLayoutEffect(() => {
    const scrollElement = scrollContainerRef.current
    if (!scrollElement) return

    // Initialize snapshot
    prevGeometryRef.current = getScrollGeometry(scrollElement)

    const handleScroll = () => {
      prevGeometryRef.current = getScrollGeometry(scrollElement)
    }

    scrollElement.addEventListener('scroll', handleScroll)

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
    }
  }, [scrollContainerRef.current])

  // ------------------------------------------------------------
  // When the caller signals that a geometry-changing event occurred
  // (e.g., zoom), adjust scroll to keep the visual anchor stable.
  // ------------------------------------------------------------
  useLayoutEffect(() => {
    const scrollElement = scrollContainerRef.current
    const prevGeometry = prevGeometryRef.current
    if (!scrollElement || !prevGeometry) return

    const nextGeometry = getScrollGeometry(scrollElement)
    const nextScrollPosition = calculatePreservedScrollPosition(prevGeometry, nextGeometry)

    if (nextScrollPosition) {
      scrollElement.scrollLeft = nextScrollPosition.x
      scrollElement.scrollTop = nextScrollPosition.y
    }

    prevGeometryRef.current = nextGeometry
  }, triggerDeps)
}

/**
 * Calculates the scroll offsets that preserve the previous visual centre after
 * the scroll area's geometry changes.
 *
 * @param prevGeometry - Geometry sampled **before** the change.
 * @param nextGeometry - Geometry sampled **after** the change.
 * @returns A `Point` containing the target `scrollLeft`/`scrollTop` values or
 *   `undefined` when preservation is not required (e.g. first render).
 */
function calculatePreservedScrollPosition(
  prevGeometry: ScrollGeometry,
  nextGeometry: ScrollGeometry
): Point | undefined {
  if (!prevGeometry) {
    return
  }

  // Where was the visual centre (in scroll-area coordinates) *before* the zoom?
  const prevVisualAnchor = atAnchor(prevGeometry.viewportRect, Anchor.center)

  // Preserve the *percentage* vertical position and re-center horizontally.
  const nextVisualAnchor: Point = {
    x: nextGeometry.scrollAreaRect.width / 2,
    y:
      (prevVisualAnchor.y / prevGeometry.scrollAreaRect.height) * nextGeometry.scrollAreaRect.height
  }

  const targetViewport = anchored({
    follower: nextGeometry.viewportRect,
    followerAnchor: Anchor.center,
    target: { ...nextVisualAnchor, height: 0, width: 0 },
    targetAnchor: Anchor.center
  })

  return {
    x: targetViewport.x,
    y: targetViewport.y
  }
}
